// Component gallery — ARN-27. Dev-only route (/gallery) that renders every owned component
// in every state, so the design system can be reviewed without seeding data.
import {
  Avatar,
  Button,
  CalendarGrid,
  Card,
  CardSkeleton,
  Chip,
  ConfirmDialog,
  DatePicker,
  EditionCoverCard,
  EmptyState,
  HeartButton,
  HoldingCard,
  Input,
  LoadingState,
  MemberRow,
  NotificationItem,
  PromptCard,
  Section,
  Select,
  Sheet,
  Skeleton,
  StatusPill,
  Switch,
  TABS,
  Textarea,
  TimelineStrip,
  todayIso,
  useToast,
} from '@roots/ui';
import { useState } from 'react';

const yearMonth = new Date().toISOString().slice(0, 7);

export function GalleryScreen() {
  const { push } = useToast();
  const [dialog, setDialog] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [hearted, setHearted] = useState(false);
  const [date, setDate] = useState<string | null>('1945-03-12');
  const [textSize, setTextSize] = useState<'default' | 'large'>('default');
  const [toggle, setToggle] = useState(true);

  return (
    <div className="flex flex-col gap-10 pb-20">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="type-display">Component gallery</h1>
          <p className="type-caption">Tokens from UI/UX §2 · components from §3</p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            const next = textSize === 'default' ? 'large' : 'default';
            setTextSize(next);
            document.documentElement.dataset.textSize = next;
          }}
        >
          Text size: {textSize}
        </Button>
      </header>

      <Section title="Type scale">
        <Card className="flex flex-col gap-2">
          <p className="type-display">Display — edition titles</p>
          <p className="type-heading">Heading — card titles</p>
          <p className="type-subheading">Subheading — section headers</p>
          <p className="type-body">Body — prompt text and descriptions</p>
          <p className="type-caption">Caption — timestamps and helper text</p>
          <p className="type-label">Label — chips and status pills</p>
        </Card>
      </Section>

      <Section title="Colour">
        <div className="flex flex-wrap gap-3">
          {[
            'bg-primary',
            'bg-surface',
            'bg-elevated',
            'accent-primary',
            'accent-secondary',
            'accent-warm-gray',
            'text-primary',
            'text-secondary',
            'text-tertiary',
            'success',
            'error',
            'heart',
            'backdrop',
            'divider',
            'today',
            'cover-wash',
          ].map((token) => (
            <div key={token} className="flex flex-col items-center gap-1">
              <span
                className="h-12 w-12 rounded-[var(--radius-sm)] border border-[var(--color-accent-warm-gray)]"
                style={{ background: `var(--color-${token})` }}
              />
              <span className="type-caption">{token}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Buttons">
        <div className="flex flex-wrap gap-2">
          <Button>Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="destructive">Destructive</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="accent">Accent (holding card only)</Button>
          <Button loading>Loading</Button>
          <Button disabled>Disabled</Button>
        </div>
      </Section>

      <Section title="Fields">
        <Card className="flex flex-col gap-3">
          <Input label="Name" placeholder="Grandpa Lou" />
          <Input label="Email" error="That doesn’t look right" defaultValue="lou@" />
          <Textarea label="Your response" placeholder="Whenever you’re ready…" />
          <Select label="Repeats" defaultValue="annual">
            <option value="annual">Every year</option>
            <option value="one_time">Just once</option>
          </Select>
          <Switch label="Drop day" checked={toggle} onCheckedChange={setToggle} />
          <Switch label="Member removed (always on)" checked disabled onCheckedChange={() => {}} />
        </Card>
      </Section>

      <Section title="Icons (§8.1)">
        <Card className="flex flex-wrap gap-6">
          {TABS.map((tab) => (
            <span key={tab.key} className="flex flex-col items-center gap-1">
              <tab.icon size={24} aria-hidden />
              <span className="type-caption">{tab.label}</span>
            </span>
          ))}
        </Card>
      </Section>

      <Section title="Pills and chips">
        <div className="flex flex-wrap items-center gap-2">
          <Chip>The Seed Family</Chip>
          <StatusPill tone="active">Prompts open</StatusPill>
          <StatusPill tone="warning">Closing in 3 days</StatusPill>
          <StatusPill tone="neutral">Between cycles</StatusPill>
          <StatusPill tone="success">New edition!</StatusPill>
          <Avatar name="Grandpa Lou" />
          <Avatar name="Ada" filled={false} />
        </div>
      </Section>

      <Section title="Prompt card">
        <PromptCard
          familyName="The Seed Family"
          promptText="Share a photo of a meal that felt like home."
          deadlineLabel="3 days left"
          urgent
          onOpen={() => push('Opened')}
        />
        <PromptCard
          familyName="The Seed Family"
          promptText="What’s a recipe that’s been passed down in your family?"
          deadlineLabel="3 days left"
          submitted
        />
      </Section>

      <Section title="Edition covers">
        <EditionCoverCard
          theme="Kitchen Table Stories"
          monthLabel="September 2026"
          familyName="The Seed Family"
          isNew
        />
        <EditionCoverCard
          theme="Harvest Time"
          monthLabel="October 2025"
          familyName="The Seed Family"
          locked
        />
      </Section>

      <Section title="Holding card (late joiner)">
        <HoldingCard
          nextMonthName="October"
          theme="Kitchen Table Stories"
          submittedCount={3}
          totalCount={7}
          occasionTeaser="Lou’s birthday on 12 September 🎂"
        />
      </Section>

      <Section title="Notifications">
        <NotificationItem
          type="drop_day"
          title="New edition!"
          body="The September edition for The Seed Family is ready to read."
          timestamp="2h ago"
          familyName="The Seed Family"
          unread
        />
        <NotificationItem
          type="heart_received"
          title="You got a heart!"
          body="Someone hearted your page."
          timestamp="Yesterday"
          familyName="The Seed Family"
        />
      </Section>

      <Section title="Roster rows">
        <ul className="flex flex-col gap-2">
          <MemberRow name="Grandpa Lou" membershipRole="admin" status="active" isEditor />
          <MemberRow name="Marcus" membershipRole="member" status="active" />
          <MemberRow name="Ada" membershipRole="member" status="holding" />
        </ul>
      </Section>

      <Section title="Hearts and motion">
        <Card className="flex items-center gap-6">
          <HeartButton
            hearted={hearted}
            count={hearted ? 4 : 3}
            onToggle={() => setHearted(!hearted)}
          />
          <Button onClick={() => push('Your response is in! ✨')}>Show a toast</Button>
        </Card>
      </Section>

      <Section title="Timeline and calendar">
        <TimelineStrip
          yearMonth={yearMonth}
          todayIso={todayIso()}
          markers={[
            { date: `${yearMonth}-12`, kind: 'occasion', label: 'Lou’s birthday on the 12th' },
            { date: `${yearMonth}-28`, kind: 'deadline', label: 'Prompt deadline on the 28th' },
            { date: `${yearMonth}-30`, kind: 'drop', label: 'Edition drop on the 30th' },
          ]}
        />
        <CalendarGrid
          yearMonth={yearMonth}
          markers={[{ date: `${yearMonth}-28`, kind: 'deadline', label: 'Prompt deadline' }]}
        />
        <Card>
          <DatePicker label="Birthday" value={date} onChange={setDate} />
        </Card>
      </Section>

      <Section title="Overlays">
        <div className="flex gap-2">
          <Button onClick={() => setDialog(true)}>Open dialog</Button>
          <Button variant="secondary" onClick={() => setSheet(true)}>
            Open sheet
          </Button>
        </div>
        <ConfirmDialog
          open={dialog}
          onOpenChange={setDialog}
          destructive
          title="Remove this member?"
          description="They’ll lose access to future editions. Past contributions stay in published editions."
          confirmLabel="Remove"
          onConfirm={() => setDialog(false)}
        />
        <Sheet open={sheet} onOpenChange={setSheet} title="Add a photo">
          <p className="type-body">Sheets rise from the bottom on mobile and centre from 768px.</p>
        </Sheet>
      </Section>

      <Section title="Paper texture (edition pages only)">
        <Card className="roots-paper min-h-32">
          <p className="type-display">Reader page</p>
          <p className="type-body">Grain sits under content; everywhere else stays flat cream.</p>
        </Card>
      </Section>

      <Section title="Loading and empty">
        <CardSkeleton />
        <LoadingState timeoutMs={0} onRetry={() => push('Retrying')} />
        <Card className="flex flex-col gap-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-1/3" />
        </Card>
        <EmptyState
          message="You’re not in any families yet."
          actions={
            <>
              <Button>Create a family</Button>
              <Button variant="secondary">I have an invite link</Button>
            </>
          }
        />
      </Section>
    </div>
  );
}
